import { memo, forwardRef, MouseEvent } from 'react'
import { useRouter } from 'next/router'
import isEqual from 'lodash/isEqual'
import { Placeholder } from 'rsuite'

import { useApi } from '../../../services/useApi'
import SeeAllCard from '../ProductCatalog/SeeAllCard'
import { createUrlParams } from '../../../lib/params'
import { getSupplierLogo, getSupplierShopPageUrl } from '../../../lib/supplier'
import {
  SupplierMappedProductsProps,
  Product,
} from '../../../utils/types/Product'
import { ProductBaseIncludes, PRODUCTS_PER_SUPPLIER, QueryParam } from '../../../utils/constants'
import SupplierCard from './SupplierCard'
import Carousel from '../../Layouts/Carousel'
import useUrls from '../../../services/useUrls'
import useGetAnalyticsLocation from '../../../services/analytics/useGetAnalyticsLocation'
import ProductCardNarrow from '../../SingleProduct/ProductCardNarrow'

import styles from '../../../styles/SupplierCarousel.module.less'
import carouselStyles from '../../../styles/CategorySpecificCarousel.module.less'

interface SupplierCarouselProps {
  supplier: SupplierMappedProductsProps
  filterProductIds?: string[]
  className?: string
  onSupplierCardClick?: (event: MouseEvent<Element>) => void
  onProductCardClick?: (product: Product, event: MouseEvent<Element>) => void
  onProductCardContextMenuClick?: (product: Product, event: MouseEvent<Element>) => void
}

const SupplierCarousel = forwardRef<HTMLDivElement, SupplierCarouselProps>((props, ref) => {
  const {
    supplier,
    filterProductIds = [],
    className = '',
    onSupplierCardClick = () => {},
  } = props

  const { query } = useRouter()
  const { getResource } = useApi()
  const { urlT } = useUrls()
  const analyticsLocation = useGetAnalyticsLocation('supplier_carousel')
  const supplierLogo = getSupplierLogo(supplier.media)

  const searchQuery = query[QueryParam.search]

  const urlQuery = createUrlParams({
    include: ProductBaseIncludes,
    filter: {
      f_supid: supplier.id,
    },
    page: { limit: searchQuery ? '48' : '12' },
  })

  const { data: products, isLoading } = getResource<Product>('product', urlQuery)

  if (products.length === 0 && !isLoading) {
    // Don't show supplier if no available products
    return null
  }

  const supplierProducts = supplier.product.length
    ? supplier.product
    : products
  const filterProducts = supplierProducts?.filter((prod) => !filterProductIds.includes(prod.id))
    ?.slice(0, PRODUCTS_PER_SUPPLIER)

  const supplierPageRedirectUrl = getSupplierShopPageUrl(supplier['supplier.code'], urlT)

  return (
    <div
      ref={ref}
      className={`${styles['supplier-carousel']} ${carouselStyles['products-wrapper']} ${className}`}
    >
      <Carousel
        scrollOffset={235}
        firstLeftScrollOffset={210}
        firstRightScrollOffset={310}
      >
        <SupplierCard
          supplierWithProducts={supplier}
          onClick={onSupplierCardClick}
          products={products}
        />
        {!isLoading && filterProducts
          ?.map((product) => (
            <ProductCardNarrow
              product={product}
              key={product['product.code']}
              analyticsContext={analyticsLocation}
              isSupplierNameVisible={false}
            />
          ))}
        {isLoading && (
          Array.from({ length: 3 }, (_, i) => (
            <Placeholder.Graph
              active
              key={i}
              className={styles['skeleton-loading-card-carousel']}
            />
          ))
        )}
        <SeeAllCard
          middleImg={supplierLogo}
          href={supplierPageRedirectUrl}
          categoryOrSupplierName={supplier['supplier.label']}
        />
      </Carousel>
    </div>
  )
})

export default memo(SupplierCarousel, isEqual)
